import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/services/api';
import {
  Plus,
  Zap,
  Trash2,
  User,
  Calendar,
  CheckCircle2,
  Circle,
} from 'lucide-react';
import clsx from 'clsx';

const EPIC_COLORS = [
  '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#EC4899', '#06B6D4', '#6366F1', '#84CC16', '#F97316',
];

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned', color: 'bg-gray-100 text-gray-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
];

export function Epics() {
  const { workspaceId, projectId } = useParams<{ workspaceId: string; projectId: string }>();
  const api = useApi();
  const queryClient = useQueryClient();

  const [selectedEpicId, setSelectedEpicId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [_isEditing, setIsEditing] = useState(false);
  const [newEpic, setNewEpic] = useState({
    name: '',
    description: '',
    color: EPIC_COLORS[0],
    status: 'planned',
    lead: '',
    startdate: '',
    targetdate: '',
  });

  const { data: epics, isLoading } = useQuery({
    queryKey: ['epics', projectId],
    queryFn: () => api.getEpics(projectId!),
    enabled: !!projectId,
  });

  const { data: workItems } = useQuery({
    queryKey: ['workItems', projectId],
    queryFn: () => api.getWorkItems(projectId!),
    enabled: !!projectId,
  });

  const { data: states } = useQuery({
    queryKey: ['states', projectId],
    queryFn: () => api.getStates(projectId!),
    enabled: !!projectId,
  });

  const { data: members } = useQuery({
    queryKey: ['members', workspaceId],
    queryFn: () => api.getMembers(workspaceId!),
    enabled: !!workspaceId,
  });

  const createEpicMutation = useMutation({
    mutationFn: (data: any) => api.createEpic(data),
    onSuccess: (epic) => {
      queryClient.invalidateQueries({ queryKey: ['epics', projectId] });
      setIsCreating(false);
      setNewEpic({
        name: '',
        description: '',
        color: EPIC_COLORS[0],
        status: 'planned',
        lead: '',
        startdate: '',
        targetdate: '',
      });
      setSelectedEpicId(epic.bh_epicid);
    },
  });

  const updateEpicMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateEpic(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epics', projectId] });
      setIsEditing(false);
    },
  });

  const deleteEpicMutation = useMutation({
    mutationFn: (id: string) => api.deleteEpic(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epics', projectId] });
      queryClient.invalidateQueries({ queryKey: ['workItems', projectId] });
      setSelectedEpicId(null);
    },
  });

  const selectedEpic = epics?.find((e: any) => e.bh_epicid === selectedEpicId);
  const epicWorkItems = workItems?.filter((wi: any) => wi.bh_epic === selectedEpicId) || [];

  const getEpicProgress = (epicId: string) => {
    const items = workItems?.filter((wi: any) => wi.bh_epic === epicId) || [];
    if (items.length === 0) return { total: 0, completed: 0, percent: 0 };
    const completed = items.filter((wi: any) => {
      const state = states?.find((s: any) => s.bh_stateid === wi._bh_state_value);
      return state?.bh_group === 4;
    }).length;
    return {
      total: items.length,
      completed,
      percent: Math.round((completed / items.length) * 100),
    };
  };

  const getMemberName = (memberId: string) =>
    members?.find((m: any) => m.bh_memberid === memberId)?.bh_name || 'Unassigned';

  const handleCreateEpic = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEpic.name.trim()) return;
    createEpicMutation.mutate({
      bh_name: newEpic.name.trim(),
      bh_description: newEpic.description,
      bh_color: newEpic.color,
      bh_status: newEpic.status,
      bh_lead: newEpic.lead || null,
      bh_project: projectId,
      bh_startdate: newEpic.startdate || null,
      bh_targetdate: newEpic.targetdate || null,
    });
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex">
      {/* Sidebar - Epic List */}
      <div className="w-80 border-r border-gray-200 bg-white flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary-600" />
              <h1 className="text-lg font-semibold text-gray-900">Epics</h1>
            </div>
            <button
              onClick={() => setIsCreating(true)}
              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-500">Group related issues into larger initiatives</p>
        </div>

        {/* Epic List */}
        <div className="flex-1 overflow-y-auto p-2">
          {epics?.length === 0 && !isCreating ? (
            <div className="text-center py-8 text-gray-500">
              <Zap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No epics yet</p>
              <button
                onClick={() => setIsCreating(true)}
                className="mt-2 text-primary-600 hover:text-primary-700 text-sm"
              >
                Create your first epic
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              {epics?.map((epic: any) => {
                const progress = getEpicProgress(epic.bh_epicid);
                const statusOption = STATUS_OPTIONS.find(s => s.value === epic.bh_status);
                const isSelected = selectedEpicId === epic.bh_epicid;

                return (
                  <button
                    key={epic.bh_epicid}
                    onClick={() => setSelectedEpicId(epic.bh_epicid)}
                    className={clsx(
                      'w-full text-left p-3 rounded-lg transition-colors',
                      isSelected ? 'bg-primary-50 border border-primary-200' : 'hover:bg-gray-50'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
                        style={{ backgroundColor: epic.bh_color }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{epic.bh_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={clsx('px-1.5 py-0.5 text-xs rounded', statusOption?.color)}>
                            {statusOption?.label}
                          </span>
                          <span className="text-xs text-gray-500">
                            {progress.completed}/{progress.total} issues
                          </span>
                        </div>
                        {progress.total > 0 && (
                          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${progress.percent}%`,
                                backgroundColor: epic.bh_color,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-gray-50">
        {isCreating ? (
          <div className="p-6">
            <div className="max-w-2xl mx-auto bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New Epic</h2>
              <form onSubmit={handleCreateEpic} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={newEpic.name}
                    onChange={(e) => setNewEpic({ ...newEpic, name: e.target.value })}
                    placeholder="Epic name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newEpic.description}
                    onChange={(e) => setNewEpic({ ...newEpic, description: e.target.value })}
                    placeholder="Describe this epic..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                    <div className="flex gap-1">
                      {EPIC_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setNewEpic({ ...newEpic, color })}
                          className={clsx(
                            'w-7 h-7 rounded-full border-2 transition-transform',
                            newEpic.color === color ? 'border-gray-800 scale-110' : 'border-transparent'
                          )}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={newEpic.status}
                      onChange={(e) => setNewEpic({ ...newEpic, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lead</label>
                    <select
                      value={newEpic.lead}
                      onChange={(e) => setNewEpic({ ...newEpic, lead: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">No lead</option>
                      {members?.map((m: any) => (
                        <option key={m.bh_memberid} value={m.bh_memberid}>{m.bh_name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
                      <input
                        type="date"
                        value={newEpic.startdate}
                        onChange={(e) => setNewEpic({ ...newEpic, startdate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target</label>
                      <input
                        type="date"
                        value={newEpic.targetdate}
                        onChange={(e) => setNewEpic({ ...newEpic, targetdate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={createEpicMutation.isPending}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    Create Epic
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCreating(false)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : selectedEpic ? (
          <>
            {/* Epic Header */}
            <div className="bg-white border-b border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: selectedEpic.bh_color }}
                  />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{selectedEpic.bh_name}</h2>
                    {selectedEpic.bh_description && (
                      <p className="text-gray-600 mt-1">{selectedEpic.bh_description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedEpic.bh_status}
                    onChange={(e) => updateEpicMutation.mutate({
                      id: selectedEpic.bh_epicid,
                      data: { bh_status: e.target.value },
                    })}
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      if (confirm('Delete this epic?')) {
                        deleteEpicMutation.mutate(selectedEpic.bh_epicid);
                      }
                    }}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Epic Meta */}
              <div className="flex items-center gap-6 mt-4 text-sm text-gray-500">
                {selectedEpic.bh_lead && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{getMemberName(selectedEpic.bh_lead)}</span>
                  </div>
                )}
                {selectedEpic.bh_startdate && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{selectedEpic.bh_startdate} → {selectedEpic.bh_targetdate || 'No target'}</span>
                  </div>
                )}
              </div>

              {/* Progress */}
              {epicWorkItems.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-medium">{getEpicProgress(selectedEpic.bh_epicid).percent}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${getEpicProgress(selectedEpic.bh_epicid).percent}%`,
                        backgroundColor: selectedEpic.bh_color,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Epic Issues */}
            <div className="flex-1 overflow-y-auto p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Issues ({epicWorkItems.length})
              </h3>
              {epicWorkItems.length === 0 ? (
                <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
                  <p className="text-gray-500">No issues in this epic yet.</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Assign issues to this epic from the issue detail view.
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {epicWorkItems.map((wi: any) => {
                    const state = states?.find((s: any) => s.bh_stateid === wi._bh_state_value);
                    const isCompleted = state?.bh_group === 4;

                    return (
                      <div key={wi.bh_workitemid} className="p-4 flex items-center gap-4">
                        {isCompleted ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-300" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={clsx(
                            'font-medium truncate',
                            isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'
                          )}>
                            <span className="text-gray-400">#{wi.bh_sequenceid}</span> {wi.bh_name}
                          </p>
                        </div>
                        <span
                          className="px-2 py-1 text-xs rounded-full text-white"
                          style={{ backgroundColor: state?.bh_color || '#6B7280' }}
                        >
                          {state?.bh_name || 'Unknown'}
                        </span>
                        {wi.bh_estimate && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {wi.bh_estimate} pts
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <Zap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">Select an epic to view details</p>
              <p className="text-sm mt-1">or create a new one</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
